import { Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCvsVanKandidaat } from '../api/cvs.ts';
import type { Kandidaat, LopendeFase, Vacature, Voordracht } from '../api/typen.ts';
import { useVoordrachtMutaties } from '../api/voordrachten.ts';
import { volledigeNaam } from '../helpers/formatteer.ts';
import { lopendeFaseLabels, opties } from '../helpers/labels.ts';
import { useVerzenden } from '../hooks/useVerzenden.ts';
import { FormulierDialoog } from './FormulierDialoog.tsx';
import { KandidaatVeld, VacatureVeld } from './keuzevelden.tsx';
import { Keuzeveld, Tekstveld } from './velden.tsx';

interface Waarden {
  kandidaat: Kandidaat | null;
  vacature: Vacature | null;
  cvId: string;
  fase: LopendeFase;
  notitie: string;
}

interface Props {
  open: boolean;
  onSluit: () => void;
  kandidaat?: Kandidaat | undefined;
  vacature?: Vacature | undefined;
  onGemaakt?: ((voordracht: Voordracht) => void) | undefined;
}

const faseOpties = opties(lopendeFaseLabels);

/**
 * Draagt een kandidaat voor op een vacature. Kandidaat of vacature kan vaststaan, bijvoorbeeld vanaf hun eigen
 * pagina; de andere kies je in de dialoog. Zonder gekozen cv gaat het actuele cv mee.
 * @param props De vaste kandidaat of vacature en wat er na het voordragen gebeurt.
 * @returns De dialoog.
 */
export function VoordrachtDialoog({ open, onSluit, kandidaat, vacature, onGemaakt }: Props) {
  const mutaties = useVoordrachtMutaties();
  const formulier = useForm<Waarden>({
    defaultValues: {
      kandidaat: kandidaat ?? null,
      vacature: vacature ?? null,
      cvId: '',
      fase: 'voorbereiding',
      notitie: '',
    },
  });
  useEffect(() => {
    if (open) {
      formulier.reset({
        kandidaat: kandidaat ?? null,
        vacature: vacature ?? null,
        cvId: '',
        fase: 'voorbereiding',
        notitie: '',
      });
    }
  }, [open, kandidaat, vacature, formulier]);

  const gekozenKandidaat = formulier.watch('kandidaat');
  const cvs = useCvsVanKandidaat(gekozenKandidaat?.id);
  const cvOpties = (cvs.data ?? []).map((cv) => ({
    waarde: cv.id,
    label: `${cv.bestandsnaam}${cv.isActueel ? ' (actueel)' : ''}`,
  }));

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      if (!waarden.kandidaat || !waarden.vacature) {
        throw new Error('Kies een kandidaat en een vacature.');
      }
      const voordracht = await mutaties.draagVoor.mutateAsync({
        kandidaatId: waarden.kandidaat.id,
        vacatureId: waarden.vacature.id,
        cvId: waarden.cvId || null,
        fase: waarden.fase,
        notitie: waarden.notitie || null,
      });
      onGemaakt?.(voordracht);
    },
    { melding: 'Kandidaat voorgedragen', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Kandidaat voordragen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        verzendLabel="Voordragen"
      >
        <Stack spacing={2}>
          {kandidaat ? (
            <Typography>Kandidaat: {volledigeNaam(kandidaat)}</Typography>
          ) : (
            <KandidaatVeld naam="kandidaat" verplicht autoFocus />
          )}
          {vacature ? (
            <Typography>Vacature: {vacature.titel}</Typography>
          ) : (
            <VacatureVeld naam="vacature" verplicht autoFocus={Boolean(kandidaat)} hulptekst="Alleen open vacatures" />
          )}
          <Keuzeveld naam="cvId" label="Cv" opties={cvOpties} leegLabel="Actuele cv" hulptekst="Welk cv meegaat" />
          <Keuzeveld naam="fase" label="Beginfase" opties={faseOpties} verplicht />
          <Tekstveld naam="notitie" label="Notitie" meerregelig rijen={3} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
